/** Keyboard shortcuts that always have a visible button twin: a binding is rendered by <KeyButton> and handled by
 * useKeyBindings, so a key without a button cannot exist. */
import { useEffect, useRef, type ReactNode } from "react";

export type KeyBinding = {
  /** KeyboardEvent.key, letters lower-case; "Space" for the space bar. */
  key: string;
  label: string;
  testId: string;
  run: () => void;
  disabled?: boolean;
};

export function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (t as HTMLInputElement).type;
    return !["button", "checkbox", "radio", "submit", "reset", "range"].includes(type);
  }
  return false;
}

export function keyOf(e: Pick<KeyboardEvent, "key">): string {
  if (e.key === " ") return "Space";
  return e.key.length === 1 ? e.key.toLowerCase() : e.key;
}

export function keyLabel(key: string): string {
  if (key === "Space") return "Space";
  if (key === "ArrowLeft") return "←";
  if (key === "ArrowRight") return "→";
  if (key === "ArrowUp") return "↑";
  if (key === "ArrowDown") return "↓";
  return key.length === 1 ? key.toUpperCase() : key;
}

/** Handles keydown on the document for the given bindings (ignored while typing or with Ctrl/Meta/Alt). */
export function useKeyBindings(bindings: readonly KeyBinding[], enabled = true): void {
  const ref = useRef(bindings);
  ref.current = bindings;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const k = keyOf(e);
      // Space/Enter on a focused button activate that button; do not double-fire a global binding.
      if ((k === "Space" || k === "Enter") && e.target instanceof HTMLElement
        && (e.target.tagName === "BUTTON" || e.target.tagName === "A" || e.target.tagName === "SUMMARY")) return;
      const b = ref.current.find((x) => x.key === k);
      if (!b || b.disabled) return;
      e.preventDefault();
      b.run();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled]);
}

export function KeyButton({ b, children, className, pressed }: {
  b: KeyBinding;
  children?: ReactNode;
  className?: string;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      className={className}
      data-testid={b.testId}
      aria-label={b.label}
      aria-keyshortcuts={b.key === "Space" ? "Space" : b.key}
      aria-pressed={pressed}
      disabled={b.disabled}
      onClick={b.run}
    >
      {children ?? b.label} <kbd aria-hidden="true">{keyLabel(b.key)}</kbd>
    </button>
  );
}
